import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { productsAPI } from '../services/api';

// Gallery images - Cloudinary CDN optimized
const sliderImages = [
  {
    url: 'https://res.cloudinary.com/dsez5ypdi/image/upload/w_800,h_800,c_fill,g_auto,q_auto,f_auto/gallery/gallery_1_market_stall.jpg',
    alt: 'Adina at Market Stall',
    caption: 'Meet the Creator'
  },
  {
    url: 'https://res.cloudinary.com/dsez5ypdi/image/upload/w_800,h_800,c_fill,g_auto,q_auto,f_auto/gallery/gallery_2_colorful_candles.jpg',
    alt: 'Colorful Candle Collection',
    caption: 'Handcrafted with Love'
  },
  {
    url: 'https://res.cloudinary.com/dsez5ypdi/image/upload/w_800,h_800,c_fill,g_auto,q_auto,f_auto/gallery/gallery_3_pink_red_candles.jpg',
    alt: 'Pink & Red Candles',
    caption: 'Beautiful Scented Creations'
  },
  {
    url: 'https://res.cloudinary.com/dsez5ypdi/image/upload/w_800,h_800,c_fill,g_auto,q_auto,f_auto/gallery/gallery_4_teddy_candles.jpg',
    alt: 'Teddy Bear Candles',
    caption: 'Unique Gift Ideas'
  },
  {
    url: 'https://res.cloudinary.com/dsez5ypdi/image/upload/w_800,h_800,c_fill,g_auto,q_auto,f_auto/gallery/gallery_5_resin_clocks.jpg',
    alt: 'Resin Clock Designs',
    caption: 'Handmade Resin Clocks'
  },
  {
    url: 'https://res.cloudinary.com/dsez5ypdi/image/upload/w_800,h_800,c_fill,g_auto,q_auto,f_auto/gallery/gallery_6_branded_candle.jpg',
    alt: 'Adinas Crafts Branded Candle',
    caption: 'Our Signature Collection'
  }
];

const Home = ({ cartCount, onAddToCart }) => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load random products as "Best Sellers" from backend API
  useEffect(() => {
    const loadBestSellers = async () => {
      try {
        // Images are now Cloudinary URLs - fast loading!
        const products = await productsAPI.getAll();
        console.log('Best Sellers - Loaded from API:', products?.length || 0);
        
        if (products && products.length > 0) {
          // Shuffle and pick up to 3 random products
          const shuffled = [...products].sort(() => Math.random() - 0.5);
          const selected = shuffled.slice(0, 3);
          console.log('Best Sellers - Selected:', selected.map(p => p.name));
          setBestSellers(selected);
        }
      } catch (error) {
        console.error('Error loading best sellers:', error);
      }
    };
    loadBestSellers();
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <SEO 
        title="Handmade Gifts UK | Artisan Candles & Crafts"
        description="Buy handmade gifts UK. Artisan soya wax candles, resin crafts, personalised gifts. Perfect for birthdays, weddings, Christmas. Free UK delivery £50+."
        keywords="handmade gifts UK, handmade home decor UK, personalised gifts UK, handmade resin crafts UK, handmade wedding gifts UK, handmade birthday gifts UK, handmade Christmas gifts UK, unique handmade presents UK, artisan handmade products UK, eco-friendly handmade gifts UK, handmade craft shop UK, handmade gift ideas UK, soya wax candles UK, elephant candle UK, teddy bear candle UK, handmade candles Aylesbury, handmade gifts Milton Keynes, artisan crafts Buckinghamshire"
        url="/"
      />
      <Navbar cartCount={cartCount} />
      
      {/* Hero Section */}
      <section className="relative bg-black min-h-[90vh] flex items-center justify-center overflow-hidden border-b border-gray-800">
        {/* Background candle image with overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1602874801006-47c1e59c364d?w=1920&h=1080&fit=crop)',
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black opacity-50" />

        {/* Animated background particles */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span className="text-white text-sm font-medium">Handcrafted with Love</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight animate-fade-in-up">
            Infuse your home with<br />charm and fragrance
          </h1>

          <p className="text-xl md:text-2xl text-amber-100 mb-4 font-serif italic animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Artisan Candles • Resin Creations
          </p>

          <p className="text-base md:text-lg text-white/90 mb-10 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            Homemade scented candles & resin designs. Unique, creative, and crafted to brighten your home or make the perfect gift.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Button
              onClick={() => navigate('/shop')}
              size="lg"
              className="bg-amber-400 hover:bg-amber-500 text-[#8B4545] font-semibold px-8 py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Shop Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => navigate('/about')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#8B4545] font-semibold px-8 py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              Our Story
            </Button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-20 bg-black border-b border-gray-800" data-testid="gallery-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-4">
              Our Creations
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Explore our handcrafted candles and stunning resin art pieces
            </p>
          </div>
          
          {/* Gallery Grid - All 6 images with lazy loading */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {sliderImages.map((image, index) => (
              <div 
                key={index} 
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 aspect-square bg-gray-800"
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  loading={index < 2 ? "eager" : "lazy"}
                  decoding="async"
                  width="400"
                  height="400"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x400?text=Image';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-center transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white font-serif text-sm">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-black border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-4">
              Why Choose Our Candles?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Each candle is lovingly handcrafted with premium materials and unique designs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Handcrafted Quality',
                description: 'Each candle is carefully hand-poured with attention to every detail',
                icon: '🕯️'
              },
              {
                title: 'Natural Ingredients',
                description: 'Made with premium soy wax and natural essential oils',
                icon: '🌿'
              },
              {
                title: 'Unique Designs',
                description: 'One-of-a-kind shapes and artistic creations you won\'t find elsewhere',
                icon: '✨'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-gray-900 border border-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300"
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-2xl font-serif font-bold text-amber-400 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers Section */}
      {bestSellers.length > 0 && (
        <section className="py-20 bg-black border-b border-gray-800" data-testid="best-sellers-section">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-amber-400 mb-4">
                Best Sellers
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Our most loved creations, handpicked for you
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {bestSellers.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddToCart={onAddToCart}
                  data-testid={`best-seller-${product.id}`}
                />
              ))}
            </div>

            <div className="text-center mt-12">
              <Button
                onClick={() => navigate('/shop')}
                size="lg"
                className="bg-amber-400 hover:bg-amber-500 text-black font-semibold px-8 py-4 text-lg rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                data-testid="view-all-products-btn"
              >
                View All Products <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* SEO Content Section - Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black border-b border-gray-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-amber-400 mb-8 text-center">
              Why Choose Adina's Crafts?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-black/50 rounded-xl p-6 border border-gray-800">
                <h3 className="text-xl font-semibold text-amber-200 mb-3">100% Handmade in UK</h3>
                <p className="text-gray-400 leading-relaxed">
                  Every candle and resin piece is lovingly handcrafted in the United Kingdom. We take pride in creating unique, 
                  artisan products that you won't find anywhere else. Our handmade candles are perfect for those who appreciate 
                  quality craftsmanship and attention to detail.
                </p>
              </div>
              
              <div className="bg-black/50 rounded-xl p-6 border border-gray-800">
                <h3 className="text-xl font-semibold text-amber-200 mb-3">Premium Soya Wax</h3>
                <p className="text-gray-400 leading-relaxed">
                  We use only 100% natural soya wax in our scented candles. Soya wax burns cleaner and longer than paraffin, 
                  releasing fragrance gradually for a more pleasant experience. Our soya wax candles are eco-friendly and 
                  produce minimal soot, making them perfect for your home.
                </p>
              </div>
              
              <div className="bg-black/50 rounded-xl p-6 border border-gray-800">
                <h3 className="text-xl font-semibold text-amber-200 mb-3">Unique Designs</h3>
                <p className="text-gray-400 leading-relaxed">
                  From adorable elephant candles and cute teddy bear candles to beautiful flower candles and elegant angel wing 
                  candles, our collection features unique designs you'll love. Each piece is a work of art, perfect as a gift 
                  or to treat yourself.
                </p>
              </div>
              
              <div className="bg-black/50 rounded-xl p-6 border border-gray-800">
                <h3 className="text-xl font-semibold text-amber-200 mb-3">Perfect Gifts</h3>
                <p className="text-gray-400 leading-relaxed">
                  Looking for unique gift ideas? Our handmade candles and resin clocks make wonderful presents for birthdays, 
                  Christmas, Mother's Day, Valentine's Day, weddings, and baby showers. We offer beautifully packaged gift sets 
                  that are ready to give.
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-serif font-bold text-amber-200 mb-4">
                Free UK Delivery on Orders Over £50
              </h3>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Enjoy free delivery across the United Kingdom when you spend £50 or more. All orders are carefully packaged 
                to ensure your handmade candles arrive in perfect condition. We ship Monday to Friday and most orders are 
                dispatched within 1-3 business days.
              </p>
              <Button
                onClick={() => navigate('/shop')}
                className="bg-amber-400 hover:bg-amber-500 text-black font-semibold px-8 py-3"
              >
                Start Shopping
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-16 bg-black border-b border-gray-800">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-serif font-bold text-amber-400 mb-8 text-center">
            Explore Our Collections
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div 
              onClick={() => navigate('/shop#homemade-scented-candles')}
              className="bg-gray-900/50 rounded-xl p-6 border border-gray-800 hover:border-amber-400/50 cursor-pointer transition-all hover:-translate-y-1"
            >
              <h3 className="text-xl font-semibold text-amber-200 mb-2">Scented Candles</h3>
              <p className="text-gray-400 text-sm">
                Hand-poured soya wax candles with beautiful fragrances. Elephant candles, teddy bears, flowers and more.
              </p>
            </div>
            
            <div 
              onClick={() => navigate('/shop#homemade-resin-designs')}
              className="bg-gray-900/50 rounded-xl p-6 border border-gray-800 hover:border-amber-400/50 cursor-pointer transition-all hover:-translate-y-1"
            >
              <h3 className="text-xl font-semibold text-amber-200 mb-2">Resin Art</h3>
              <p className="text-gray-400 text-sm">
                Unique handmade resin clocks and decorations. Each piece is one-of-a-kind, perfect for your home.
              </p>
            </div>
            
            <div 
              onClick={() => navigate('/shop#perfect-gift')}
              className="bg-gray-900/50 rounded-xl p-6 border border-gray-800 hover:border-amber-400/50 cursor-pointer transition-all hover:-translate-y-1"
            >
              <h3 className="text-xl font-semibold text-amber-200 mb-2">Gift Sets</h3>
              <p className="text-gray-400 text-sm">
                Beautifully curated gift boxes for any occasion. Birthday, Christmas, wedding or just because.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;